/**
 * GelPlatformConnectorsPageV2 — proper v2 redesign of GEL Platform
 * Connectors (G125).
 *
 * Canonical inventory pattern.
 * Reuses every backend code path from v1 GelPlatformConnectorsPage:
 *   - api.gelListConnectors / gelGetConnector
 *   - api.gelCreateConnector / gelUpdateConnector / gelDeleteConnector
 *   - api.gelTestConnector
 *
 * v1 stays as fallback via /gel-connectors?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import * as api from '../services/api';
import {
  RefreshCw, Plus, Server, Globe2, CheckCircle, XCircle, Settings, Trash2,
  Play, Link2, Users,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  MetricCard, TenantScopePicker, ConnectorAssignTenantsSlideout,
  ConnectorOwnerPicker,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import EntityLabel from '../components/common/EntityLabel';
import { EntityPicker } from '../components/v2/EntityPicker';
import { FRAMEWORK_TYPES, FRAMEWORK_CHOICES, FRAMEWORK_TABS, isCodedFramework } from '../components/v2/frameworkCatalog';

import GelPlatformConnectorsPage from './GelPlatformConnectorsPage';

const STATUS_OPTIONS = [
  { key: 'active',     label: 'Active',     tone: 'success' },
  { key: 'inactive',   label: 'Inactive',   tone: 'muted' },
  { key: 'error',      label: 'Error',      tone: 'danger' },
  { key: 'configuring', label: 'Configuring', tone: 'warning' },
];
const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.tone]));

/* Framework catalog (types + tabbed picker choices) is shared — see components/v2/frameworkCatalog.js */

const AUTH_OPTIONS = [
  { key: 'API_KEY',         label: 'API Key' },
  { key: 'OAUTH2',          label: 'OAuth 2.0' },
  { key: 'SERVICE_ACCOUNT', label: 'Service Account' },
  { key: 'BEARER_TOKEN',    label: 'Bearer Token' },
  { key: 'MTLS',            label: 'mTLS' },
  { key: 'CUSTOM',          label: 'Custom' },
];

const CONNECTOR_FORM_DEFAULT = {
  name: '',
  frameworkChoice: 'OPENAI',   // EntityPicker row id (maps to platformType + optional preset)
  platformType: 'OPENAI',
  baseUrl: '',
  authMethod: 'API_KEY',
  authConfig: '',
  description: '',
  defaultModel: '',
  defaultMaxTokens: 2048,
  defaultTimeoutSeconds: 30,
  rateLimitRpm: 60,
  maxConcurrency: 10,
  // CONN-OWNER — explicit owner Org + Tenant for the new connector.
  // For Tenant Admin this auto-fills from session and the picker is hidden;
  // for higher roles the operator picks. Required at submit time.
  ownerOrgId: '',
  ownerTenantId: '',
  // V226 + V227 / CONN-SCOPE — additional tenants to share with on create.
  // Empty means owner-tenant-only (existing behavior). Granular shape:
  // [{tenantId, instanceIds[]}] — empty instanceIds means cascade (all
  // instances under that tenant). Org/Super/Platform Admin only — Tenant
  // Admin never sees the picker.
  scopeGrants: [],
};

/* V226 / CONN-SCOPE — roles allowed to grant cross-tenant scope on
 * connectors. Mirrors PlatformConnectorService.canGrantScope() on the BE;
 * if these strings ever drift, the FE picker may render but the BE rejects
 * the payload — which is the correct safety posture, never the inverse. */
const SCOPE_GRANT_ROLES = new Set(['Org Admin', 'Super Admin', 'Platform Admin']);

function GelPlatformConnectorsPageV2Inner() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { session } = useAuth();
  const tenantId = session?.tenantId;
  const orgId = session?.orgId;
  /* V226 / CONN-SCOPE — drives whether the create form's TenantScopePicker
   * + the row-level "Assign to tenants" action surface. The BE enforces
   * the same gate (returns 403 to Tenant Admin); the FE just hides the
   * affordance so we don't dangle a button that can't be used. */
  const canGrantScope = SCOPE_GRANT_ROLES.has(session?.role);

  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);

  /* V226 / CONN-SCOPE — row-level Assign-to-tenants slideout state.
   * Holds the connector row + its current shared-with set so the slideout
   * opens pre-populated. Cleared on close + after a successful save. */
  const [assignTenantsRow, setAssignTenantsRow] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.gelListConnectors?.(tenantId);
      /* api.gelListConnectors returns connectors already unwrapped from their
       * sharing envelope, as a plain array. Do not decode here. */
      const list = Array.isArray(res?.data) ? res.data : [];
      /* V226 / CONN-SCOPE — the BE now returns each row wrapped as
       * { connector: {...}, isOwner: bool, sharedWithTenantIds: [...] }.
       * Flatten to the legacy shape the rest of the page expects, but
       * carry the two new fields through so the row actions can render
       * the share count + open the assign-slideout pre-populated.
       * Pre-V226 server (or unwrapped responses) keep working — we just
       * default isOwner=true (sane for the most common case) and
       * sharedWithTenantIds=[]. */
      setConnectors(list);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  useEffect(() => { reload(); }, [reload]);

  /* Tenant-name lookup map used by the Overview slideout's "Shared with"
   * section. We render UUIDs as a fallback when names aren't resolvable
   * (e.g. when the caller's role doesn't grant them tenant.read across
   * the share targets, or a tenant was deleted after the grant was made).
   * Loaded once on mount; refreshed alongside the connector list. */
  const [tenantNamesById, setTenantNamesById] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = typeof api.getTenants === 'function' ? await api.getTenants() : null;
        const items = Array.isArray(res?.data) ? res.data
                    : Array.isArray(res?.items) ? res.items
                    : Array.isArray(res) ? res : [];
        if (cancelled) return;
        const map = {};
        for (const t of items) {
          if (!t || !t.id) continue;
          map[t.id] = t.name || t.displayName || t.id;
        }
        setTenantNamesById(map);
      } catch (e) { /* non-fatal — UI falls back to UUIDs */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const resolveTenantName = useCallback(
    (id) => tenantNamesById[id] || (id ? `${String(id).slice(0, 8)}…` : '—'),
    [tenantNamesById]
  );

  const [facets, setFacets] = useState(() => ({
    status: new Set(),
    platformType: new Set(),
    authMethod: new Set(),
  }));
  const toggleFacet = (gKey, oKey, c) => setFacets(p => { const n = new Set(p[gKey] || []); c ? n.add(oKey) : n.delete(oKey); return { ...p, [gKey]: n }; });
  const clearFacets = () => setFacets({ status: new Set(), platformType: new Set(), authMethod: new Set() });

  const FACET_GROUPS = useMemo(() => ([
    { key: 'status',       label: 'Status',        options: STATUS_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
    /* Display label says "Framework" — disambiguates from the platform's
     * sidebar "Connectors" concept (Slack, Salesforce, etc., outbound
     * integrations agents use). Internal field name `platformType` is
     * unchanged for backend wire compatibility. */
    { key: 'platformType', label: 'Framework',     options: FRAMEWORK_TYPES },
    { key: 'authMethod',   label: 'Auth',          options: AUTH_OPTIONS },
  ]), []);

  /* G215 — Backend PlatformConnectorEntity returns status in mixed case
   * ("ACTIVE" by default), while V2 STATUS_OPTIONS keys are lowercase.
   * Normalize via lowercase comparison so the facet count matches what
   * KPIs already show via the `norm()` helper at the bottom of the file. */
  const valueFor = (c, key) =>
      key === 'status' ? (c?.status || '').toString().toLowerCase()
    : c?.[key];

  const filtered = useMemo(() => connectors.filter(c => {
    if (facets.status.size > 0 && !facets.status.has(valueFor(c, 'status'))) return false;
    if (facets.platformType.size > 0 && !facets.platformType.has(c.platformType)) return false;
    if (facets.authMethod.size > 0 && !facets.authMethod.has(c.authMethod)) return false;
    return true;
  }), [connectors, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  const facetCounts = useMemo(() => {
    const counts = {};
    for (const g of FACET_GROUPS) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = connectors.filter(c => {
          for (const og of FACET_GROUPS) {
            if (og.key === g.key) continue;
            const sel = facets[og.key]; if (!sel || sel.size === 0) continue;
            if (!sel.has(valueFor(c, og.key))) return false;
          }
          return valueFor(c, g.key) === opt.key;
        }).length;
      }
    }
    return counts;
  }, [connectors, facets, FACET_GROUPS]);

  const facetGroupsWithCounts = useMemo(() => FACET_GROUPS.map(g => ({
    ...g, options: g.options.map(o => ({ ...o, count: facetCounts[g.key]?.[o.key] || 0, checked: facets[g.key]?.has(o.key) || false })),
  })), [facetCounts, facets, FACET_GROUPS]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS) for (const optKey of facets[g.key] || []) {
      const opt = g.options.find(o => o.key === optKey);
      chips.push({ groupKey: g.key, optionKey: optKey, label: `${g.label}: ${opt?.label || optKey}` });
    }
    return chips;
  }, [facets, FACET_GROUPS]);

  const [density, setDensity] = useState(() => { try { return localStorage.getItem('gel-conn-density') || 'comfortable'; } catch { return 'comfortable'; } });
  const cycleDensity = () => { const n = density === 'comfortable' ? 'compact' : 'comfortable'; setDensity(n); try { localStorage.setItem('gel-conn-density', n); } catch {} };

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('overview');
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('overview'); };
  const closeSlideout = () => setSelected(null);

  const handleTest = async (id) => {
    try {
      const r = await api.gelTestConnector?.(id);
      /* Trust the apiFetch envelope's HTTP-level ok as the primary signal.
       * Previous version only checked data.ok/success/status==='OK', which
       * silently misclassified every BE that returns {message, reachable}
       * or {healthStatus}-style payloads — operators got a "test failed"
       * toast with a success message in the body. Now: HTTP 2xx → pass;
       * data.ok === false / status === 'ERROR' explicitly downgrades to
       * fail; everything else trusts the HTTP layer. */
      const httpOk = r && r.ok !== false;
      const data = r?.data || r;
      const explicitFail = data?.ok === false
        || data?.success === false
        || data?.status === 'ERROR'
        || data?.status === 'FAIL'
        // The backend (PlatformConnectorController.testConnectorHealth) returns
        // HealthCheckResult with field name `status`, NOT `healthStatus`.
        // Without this match, every UNHEALTHY result flows through as "passed"
        // with the failure message in the description — user-visible
        // contradiction (toast title says "Connector test passed" while body
        // says "Health check failed: Missing or invalid API key").
        || data?.status === 'UNHEALTHY'
        || data?.healthStatus === 'UNHEALTHY';
      if (httpOk && !explicitFail) {
        addToast({
          tone: 'success',
          title: 'Connector test passed',
          description: data?.message || 'Connection succeeded.',
        });
      } else {
        addToast({
          tone: 'warning',
          title: 'Connector test failed',
          description: data?.message || r?.error || 'Connection refused or auth invalid',
        });
      }
    } catch (err) { addToast({ tone: 'danger', title: 'Test failed', description: err?.message }); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this External Agent Framework? Active registrations using it will lose access.')) return;
    try { await api.gelDeleteConnector?.(id); addToast({ tone: 'info', title: 'Connector deleted' }); closeSlideout(); reload(); }
    catch (err) { addToast({ tone: 'danger', title: 'Delete failed', description: err?.message }); }
  };

  /* Create modal state */
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(CONNECTOR_FORM_DEFAULT);
  const [creating, setCreating] = useState(false);

  const openCreate = () => {
    setForm({ ...CONNECTOR_FORM_DEFAULT });
    setShowCreate(true);
  };

  /* G205 — V2-native Configure (edit) modal state. Calls api.gelUpdateConnector.
   * platformType is immutable per backend contract — render as read-only.
   * The auth config field shows the current operational defaults that v1
   * stores nested INSIDE authConfig (defaultModel / defaultMaxTokens /
   * defaultTimeoutSeconds), and lets the operator re-paste a vault://
   * reference for the credential rotation flow. */
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);

  const openEdit = (c) => {
    if (!c) return;
    const ac = c.authConfig || {};
    setEditId(c.id);
    setEditForm({
      name: c.name || '',
      platformType: c.platformType || '',
      baseUrl: c.endpointUrl || c.baseUrl || '',
      authMethod: c.authMethod || 'API_KEY',
      authConfig: (() => {
        /* Strip operational nested fields out before showing JSON to the
         * operator so they don't accidentally edit them in two places. */
        const cleaned = { ...ac };
        delete cleaned.defaultModel;
        delete cleaned.defaultMaxTokens;
        delete cleaned.defaultTimeoutSeconds;
        try { return Object.keys(cleaned).length ? JSON.stringify(cleaned, null, 2) : ''; }
        catch { return ''; }
      })(),
      defaultModel: ac.defaultModel || '',
      defaultMaxTokens: ac.defaultMaxTokens || c.defaultMaxTokens || 2048,
      defaultTimeoutSeconds: ac.defaultTimeoutSeconds || c.defaultTimeoutSeconds || 30,
      rateLimitRpm: c.maxRequestsPerMinute || c.rateLimitRpm || 60,
      maxConcurrency: c.maxConcurrentRequests || c.maxConcurrency || 10,
      description: c.description || '',
    });
    setEditError(null);
    setShowEdit(true);
  };

  const submitEdit = async (e) => {
    e?.preventDefault?.();
    if (!editForm || !editId) return;
    setEditError(null);
    if (!editForm.name.trim()) { setEditError('Connector name is required.'); return; }
    // Coded-SDK frameworks have no inbound endpoint.
    if (!isCodedFramework(editForm.platformType) && !editForm.baseUrl.trim()) { setEditError('Base URL is required.'); return; }
    let parsedAuthConfig = {};
    if (editForm.authConfig.trim()) {
      try { parsedAuthConfig = JSON.parse(editForm.authConfig); }
      catch { setEditError('Auth config must be valid JSON.'); return; }
    }
    const enriched = { ...parsedAuthConfig };
    if (editForm.defaultModel) enriched.defaultModel = editForm.defaultModel;
    if (editForm.defaultMaxTokens) enriched.defaultMaxTokens = parseInt(editForm.defaultMaxTokens, 10);
    if (editForm.defaultTimeoutSeconds) enriched.defaultTimeoutSeconds = parseInt(editForm.defaultTimeoutSeconds, 10);

    /* Backend forbids platformType on update — drop it from payload. */
    const payload = {
      name: editForm.name,
      endpointUrl: editForm.baseUrl,
      authMethod: editForm.authMethod,
      authConfig: enriched,
      description: editForm.description || '',
      maxConcurrentRequests: parseInt(editForm.maxConcurrency, 10) || 10,
      maxRequestsPerMinute: parseInt(editForm.rateLimitRpm, 10) || 60,
    };
    setEditSaving(true);
    try {
      // eslint-disable-next-line no-console
      console.info('[gel-connector edit] PUT', editId, payload);
      const res = await api.gelUpdateConnector(editId, payload);
      // eslint-disable-next-line no-console
      console.info('[gel-connector edit] response', res);
      if (res && res.ok === false) {
        const msg = res?.message || res?.error || `Update failed (HTTP ${res?.status || '?'}).`;
        setEditError(msg);
        // Also fire a toast — the inline error banner can fall below the
        // scroll fold on long forms, leaving the operator wondering why
        // Save did nothing. Toast guarantees they see the failure.
        addToast({ tone: 'danger', title: 'Update failed', description: msg });
        return;
      }
      addToast({ tone: 'success', title: 'Connector updated' });
      setShowEdit(false);
      reload();
      /* Keep the slideout open, showing what was actually saved. Re-fetching is what
       * keeps the Configure form — which is seeded from this object — in step with the
       * server rather than with the state captured when the slideout was opened. */
      if (selected?.id === editId) {
        try {
          const fresh = await api.gelGetConnector(editId);
          if (fresh?.ok && fresh.data) {
            setSelected((prev) => ({ ...prev, ...fresh.data }));
          }
        } catch (refreshErr) {
          /* The save succeeded; only the refresh failed. Surface it rather than leaving
           * the operator looking at stale values with no indication they are stale. */
          // eslint-disable-next-line no-console
          console.warn('[gel-connector edit] post-save refresh failed', refreshErr);
          addToast({
            tone: 'warning',
            title: 'Saved, but the view may be out of date',
            description: 'Your changes were saved. Reload the page to see the current values.',
          });
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[gel-connector edit] exception', err);
      const msg = err?.message || String(err);
      setEditError(msg);
      addToast({ tone: 'danger', title: 'Update failed', description: msg });
    } finally {
      setEditSaving(false);
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      addToast({ tone: 'danger', title: 'Connector name required' }); return;
    }
    // Coded-SDK frameworks have no inbound endpoint — the agent calls the gateway in-process.
    if (!isCodedFramework(form.platformType) && !form.baseUrl.trim()) {
      addToast({ tone: 'danger', title: 'Base URL required' }); return;
    }
    /* CONN-OWNER — explicit owner Org + Tenant required. For Tenant Admin
     * the picker auto-fills from session before submit; for higher roles
     * the operator must pick. Surface a precise error if either is empty. */
    if (!form.ownerOrgId || !form.ownerTenantId) {
      addToast({
        tone: 'danger',
        title: 'Owner required',
        description: 'Pick the Org + Tenant that will own this connector before saving.',
      });
      return;
    }
    let parsedAuthConfig = {};
    if (form.authConfig.trim()) {
      try { parsedAuthConfig = JSON.parse(form.authConfig); }
      catch { addToast({ tone: 'danger', title: 'Auth config must be valid JSON' }); return; }
    }

    /* Backend payload shape (mirrors v1 GelPlatformConnectorsPage:307-315).
     *   - top-level uses `endpointUrl`, `maxConcurrentRequests`, `maxRequestsPerMinute`
     *   - `defaultModel` / `defaultMaxTokens` / `defaultTimeoutSeconds`
     *     are nested INSIDE authConfig (connector-level operational config).
     *   - `platformType` is only valid on create, never on update.
     */
    const enrichedAuthConfig = { ...parsedAuthConfig };
    if (form.defaultModel) enrichedAuthConfig.defaultModel = form.defaultModel;
    if (form.defaultMaxTokens) enrichedAuthConfig.defaultMaxTokens = parseInt(form.defaultMaxTokens, 10);
    if (form.defaultTimeoutSeconds) enrichedAuthConfig.defaultTimeoutSeconds = parseInt(form.defaultTimeoutSeconds, 10);

    const payload = {
      platformType: form.platformType,
      name: form.name,
      endpointUrl: form.baseUrl,
      authMethod: form.authMethod,
      authConfig: enrichedAuthConfig,
      description: form.description || `${form.name} — ${form.platformType} external agent framework`,
      maxConcurrentRequests: parseInt(form.maxConcurrency, 10) || 10,
      maxRequestsPerMinute: parseInt(form.rateLimitRpm, 10) || 60,
      /* CONN-OWNER — explicit owner overrides session.tenantId/orgId at
       * the BE. Backend resolves session fallback when these are absent,
       * but we always send them now since the FE collects them deliberately. */
      ownerOrgId: form.ownerOrgId,
      ownerTenantId: form.ownerTenantId,
    };
    /* V226 + V227 / CONN-SCOPE — two-step share on create:
     *   1) The CreateConnectorWithScopeRequest record only accepts the
     *      legacy scopeTenantIds (cascade-only) field. We extract cascade
     *      tenants from the picker's granular grants and ship those in
     *      the create payload so the create flow still works in one round
     *      trip for the common (cascade-only) case.
     *   2) After create succeeds, if the picker had any per-instance
     *      grants, we follow up with assignTenantGrants on the new id.
     * This keeps create endpoint backward compat AND surfaces the V226
     * per-instance affordance at create time. */
    if (canGrantScope && Array.isArray(form.scopeGrants) && form.scopeGrants.length > 0) {
      const cascadeTenants = form.scopeGrants
        .filter((g) => !g.instanceIds || g.instanceIds.length === 0)
        .map((g) => g.tenantId);
      if (cascadeTenants.length > 0) payload.scopeTenantIds = cascadeTenants;
    }

    setCreating(true);
    try {
      const res = await api.gelCreateConnector(payload);
      // apiFetch returns { ok: false, ... } for some non-2xx responses (e.g. 409 dup);
      // make sure we only toast success when the create actually succeeded.
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Create failed',
          description: res?.message || res?.error || 'Backend rejected payload' });
        return;
      }
      /* V227 / CONN-SCOPE — second step of the two-step share: per-instance
       * grants. If the picker selected any per-instance restrictions, fire a
       * follow-up assignTenantGrants on the freshly-created connector. */
      // The create endpoint returns the connector entity itself, not a
      // sharing envelope — the envelope only wraps the list/detail responses.
      const newConnector = (res && 'data' in res) ? res.data : res;
      const newId = newConnector?.id;
      const perInstanceGrants = (canGrantScope && Array.isArray(form.scopeGrants))
        ? form.scopeGrants.filter((g) => Array.isArray(g.instanceIds) && g.instanceIds.length > 0)
        : [];
      if (newId && perInstanceGrants.length > 0) {
        try {
          await api.gelAssignConnectorTenantGrants(newId, perInstanceGrants);
        } catch (grantErr) {
          // Connector created; only the per-instance refinement failed. Don't
          // block the success toast — surface a follow-up warning so the
          // operator can re-try via the row action.
          addToast({ tone: 'warning', title: 'Per-instance share partially failed',
            description: `Connector created, but per-instance grants didn't apply: ${grantErr?.message || String(grantErr)}. Use "Assign to tenants" on the row to retry.` });
        }
      }
      addToast({ tone: 'success', title: 'External Agent Framework created',
        description: 'You can now register external agents against this framework.' });
      setShowCreate(false);
      reload();
    } catch (err) {
      addToast({ tone: 'danger', title: 'Create failed', description: err?.message });
    } finally {
      setCreating(false);
    }
  };

  /* G143 — backend returns status in UPPER_CASE ("ACTIVE", "DISABLED", "ERROR")
   * so we case-insensitively compare. The status pill on each row already
   * lowercases for display via the Pill component, but the KPI tiles were
   * doing literal === 'active' and reporting 0 every time. */
  const norm = (s) => (s || '').toString().toLowerCase();
  const kpis = useMemo(() => ({
    total: connectors.length,
    active: connectors.filter(c => norm(c.status) === 'active').length,
    error: connectors.filter(c => norm(c.status) === 'error').length,
    distinctPlatforms: new Set(connectors.map(c => c.platformType).filter(Boolean)).size,
  }), [connectors]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">External Agent Frameworks</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Configured frameworks that ExteLet registrations bind to. {kpis.total} total · {kpis.active} active · {filtered.length} match filters.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
          <Button variant="secondary" size="sm" iconLeft={<Globe2 size={13} />}
            onClick={() => navigate('/gel-agents')}>
            Registrations
          </Button>
          <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
            onClick={openCreate}>
            Add External Agent Framework
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
        <MetricCard label="Total" value={kpis.total} tone="cyan" />
        <MetricCard label="Active" value={kpis.active} tone="success" />
        <MetricCard label="In error" value={kpis.error} tone={kpis.error > 0 ? 'danger' : 'success'} />
        <MetricCard label="Platforms" value={kpis.distinctPlatforms} tone="trust" />
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button key={`${c.groupKey}:${c.optionKey}`} onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white">
              {c.label} ×
            </button>
          ))}
          <button onClick={clearFacets} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">Clear all</button>
        </div>
      )}

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail groups={facetGroupsWithCounts} onToggle={toggleFacet} ariaLabel="Connector filters" />

        {filtered.length === 0 && !loading ? (
          <EmptyState
            icon={<Server size={20} />}
            title={connectors.length === 0 ? 'No External Agent Frameworks yet' : 'No frameworks match filters'}
            description={connectors.length === 0
              ? 'Step 1 — add an External Agent Framework (OpenAI, Anthropic, Azure OpenAI, Vertex AI, AgentForce, ServiceNow, UiPath, LangChain, CrewAI, AutoGen, Custom REST/gRPC/MCP). External agents (ExteLets) will register against these frameworks and inherit their credentials and routing.'
              : 'Adjust filters or clear them to see more.'}
            primaryAction={connectors.length === 0
              ? <Button variant="primary" iconLeft={<Plus />} onClick={openCreate}>Add External Agent Framework</Button>
              : <Button variant="primary" onClick={clearFacets}>Clear filters</Button>}
          />
        ) : (
          <Table
            ariaLabel="External Agent Frameworks"
            density={density}
            columns={[
              { key: 'name', header: 'Framework', sortable: true, render: (r) => (
                <div>
                  <div className="font-semibold text-v2-ink">{r.name || r.id}</div>
                  <div className="text-[10.5px] text-v2-ink-muted">{r.description?.slice(0, 60) || '—'}</div>
                </div>
              )},
              { key: 'platformType', header: 'Platform', render: (r) => (
                <Pill tone="cyan" size="sm">{r.platformType || '—'}</Pill>
              )},
              { key: 'auth', header: 'Auth', render: (r) => (
                <Pill tone="muted" size="sm">{r.authMethod || '—'}</Pill>
              )},
              { key: 'endpoint', header: 'Endpoint', render: (r) => (
                <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">{(r.endpointUrl || r.baseUrl || '').slice(0, 40) || '—'}</span>
              )},
              { key: 'status', header: 'Status', render: (r) => (
                <Pill tone={STATUS_TONE[valueFor(r, 'status')] || 'muted'} size="sm" dot>{valueFor(r, 'status') || 'unknown'}</Pill>
              )},
              { key: 'shared', header: 'Shared with', render: (r) => {
                const count = Array.isArray(r.__sharedWithTenantIds) ? r.__sharedWithTenantIds.length : 0;
                if (!r.__isOwner) return <Pill tone="muted" size="sm">shared in</Pill>;
                if (count === 0) return <span className="text-[11px] text-v2-ink-muted">owner only</span>;
                return (
                  <Pill tone="info" size="sm" title={`Shared with ${count} additional tenant${count === 1 ? '' : 's'}`}>
                    +{count} tenant{count === 1 ? '' : 's'}
                  </Pill>
                );
              }},
              { key: 'actions', header: '', align: 'right', render: (r) => (
                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => handleTest(r.id)} title="Test connection">
                    <Play size={13} />
                  </Button>
                  {/* V226 / CONN-SCOPE — only owner + scope-grant-capable role
                    * sees the "Assign tenants" action. Shared-into rows are
                    * read-only on this surface; their owner controls scope. */}
                  {canGrantScope && r.__isOwner && (
                    <Button variant="ghost" size="sm"
                      onClick={() => setAssignTenantsRow(r)} title="Assign to tenants">
                      <Users size={13} />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(r)} title="Configure"
                    disabled={!r.__isOwner}>
                    <Settings size={13} />
                  </Button>
                  <Button variant="ghost" size="sm" className="!text-v2-danger hover:!bg-v2-danger-soft"
                    onClick={() => handleDelete(r.id)} title={r.__isOwner ? 'Delete' : 'Only owner tenant can delete'}
                    disabled={!r.__isOwner}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              )},
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="External Agent Frameworks"
          />
        </div>
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} detail` : 'Connector detail'}
        width={520}
        tabs={[
          { id: 'overview',  label: 'Overview' },
          { id: 'auth',      label: 'Auth + endpoint' },
          { id: 'skills',    label: 'Skills exposed' },
          { id: 'usage',     label: 'Bound registrations' },
          { id: 'audit',     label: 'Audit' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={STATUS_TONE[valueFor(selected, 'status')] || 'muted'} size="sm" dot>{valueFor(selected, 'status') || 'unknown'}</Pill>
              <Pill tone="cyan" size="sm">{selected.platformType}</Pill>
              <Pill tone="muted" size="sm">{selected.authMethod}</Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
            <p className="text-[12px] text-v2-ink-secondary">{selected.description?.slice(0, 100) || '—'}</p>
            <div className="flex items-center gap-2 mt-3">
              <Button variant="primary" size="sm" iconLeft={<Play size={13} />} onClick={() => handleTest(selected.id)}>Test connection</Button>
              <Button variant="secondary" size="sm" iconLeft={<Settings size={13} />} onClick={() => openEdit(selected)}>Configure</Button>
              <Button variant="ghost" size="sm" className="!text-v2-danger" iconLeft={<Trash2 size={13} />} onClick={() => handleDelete(selected.id)}>Delete</Button>
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'overview' && (
          <>
            <Card padding="sm">
              <Card.Header title="Connector overview" />
              <Card.Body>
                <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Framework</dt><dd className="text-v2-ink mt-0.5">{selected.platformType}</dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Auth method</dt><dd className="text-v2-ink mt-0.5">{selected.authMethod}</dd></div>
                  <div className="col-span-2"><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Endpoint URL</dt><dd className="text-v2-ink mt-0.5 font-mono-v2 text-[11px]">{selected.endpointUrl || selected.baseUrl || '—'}</dd></div>
                  {/* RC-25 / R1 — Owner tenant + org chips now route through
                      EntityLabel for the platform-standard "{name} ({shortid})"
                      render. Previously the org cell showed bare shortid with
                      no name lookup at all. */}
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Owner tenant</dt><dd className="mt-0.5"><EntityLabel kind="tenant" id={selected.tenantId} name={tenantNamesById[selected.tenantId]} size="sm" /></dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Owner org</dt><dd className="mt-0.5"><EntityLabel kind="org" id={selected.orgId} size="sm" /></dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Created</dt><dd className="text-v2-ink mt-0.5">{selected.createdAt ? new Date(selected.createdAt).toLocaleString() : '—'}</dd></div>
                  <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Last test</dt><dd className="text-v2-ink mt-0.5">{selected.lastTestedAt ? new Date(selected.lastTestedAt).toLocaleString() : 'never'}</dd></div>
                </dl>
              </Card.Body>
            </Card>

            {/* Shared-with detail. The list view's "+N tenant" pill points the
              * operator here for who exactly. Owners + admins see the full
              * roster; shared-into tenants cannot enumerate peers (the BE
              * returns __sharedWithGrants=[] for them — empty state below). */}
            <Card padding="sm" className="mt-3">
              <Card.Header
                title={`Shared with · ${(selected.__sharedWithGrants || selected.__sharedWithTenantIds || []).length} tenant${(selected.__sharedWithGrants || selected.__sharedWithTenantIds || []).length === 1 ? '' : 's'}`}
                actions={selected.__isOwner ? (
                  <Button variant="ghost" size="sm" iconLeft={<Users size={12} />}
                          onClick={() => setAssignTenantsRow(selected)}>
                    Manage
                  </Button>
                ) : null}
              />
              <Card.Body>
                {(() => {
                  const grants = Array.isArray(selected.__sharedWithGrants) ? selected.__sharedWithGrants : [];
                  const legacyIds = Array.isArray(selected.__sharedWithTenantIds) ? selected.__sharedWithTenantIds : [];
                  if (grants.length === 0 && legacyIds.length === 0) {
                    return (
                      <p className="text-[12px] text-v2-ink-muted leading-relaxed">
                        {selected.__isOwner
                          ? 'Not shared with any other tenant. Click Manage to grant access to additional tenants — they will see this connector in their own External Agent Frameworks list and can bind ExteLets to it.'
                          : 'You\'re viewing this as a shared-in tenant; the full share roster is hidden by policy. Ask the owner tenant if you need the peer list.'}
                      </p>
                    );
                  }
                  // Prefer the V227 rich-grant shape (per-instance restrictions);
                  // fall back to the flat tenant-id list when grants weren't
                  // populated (older BE response).
                  const rows = grants.length > 0
                    ? grants.map((g) => ({ tenantId: g.tenantId, instanceIds: g.instanceIds || [] }))
                    : legacyIds.map((id) => ({ tenantId: id, instanceIds: [] }));
                  return (
                    <ul className="space-y-1.5 text-[12.5px]">
                      {rows.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 py-1 border-b border-v2-line last:border-b-0">
                          <Users size={12} className="text-v2-ink-muted mt-1 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-v2-ink truncate" title={r.tenantId}>
                              {resolveTenantName(r.tenantId)}
                            </div>
                            <div className="text-[10.5px] text-v2-ink-muted font-mono-v2 truncate" title={r.tenantId}>
                              {r.tenantId}
                            </div>
                          </div>
                          <div className="text-[10.5px] text-v2-ink-muted text-right flex-shrink-0">
                            {r.instanceIds.length === 0
                              ? <Pill tone="muted" size="sm">all instances</Pill>
                              : <Pill tone="info" size="sm">{r.instanceIds.length} instance{r.instanceIds.length === 1 ? '' : 's'}</Pill>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </Card.Body>
            </Card>
          </>
        )}
        {selected && slideoutTab === 'auth' && (
          <Card padding="sm">
            <Card.Header title="Auth configuration" actions={
              <Button variant="ghost" size="sm" onClick={() => openEdit(selected)}>Edit / rotate</Button>
            } />
            <Card.Body>
              <p className="text-[12.5px] text-v2-ink-secondary leading-relaxed">
                Auth method: <strong className="text-v2-ink">{selected.authMethod}</strong>.
                Credentials are stored encrypted in the platform's secrets store; values are not displayed here.
              </p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'skills' && (
          <Card padding="sm">
            <Card.Header title="Skills this connector exposes" />
            <Card.Body>
              {(selected.exposedSkills || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No skills declared by this connector.</p>
              ) : (
                <ul className="space-y-1.5 text-[12.5px]">
                  {(selected.exposedSkills || []).map((s, i) => (
                    <li key={i} className="text-v2-ink"><strong>{s.name || s.id}</strong>{s.description && <span className="text-v2-ink-muted"> · {s.description}</span>}</li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'usage' && (
          <Card padding="sm">
            <Card.Header title="Bound registrations" actions={
              <Button variant="ghost" size="sm" onClick={() => navigate('/gel-agents')}>Open registrations →</Button>
            } />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">{selected.boundRegistrations || 0} ExteLet registration{selected.boundRegistrations === 1 ? '' : 's'} use this connector.</p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'audit' && (
          <Card padding="sm">
            <Card.Header title="Audit trail" />
            <Card.Body>
              <Button variant="ghost" size="sm" onClick={() => navigate(`/workflow-audit?connectorId=${selected.id}`)}>Open full audit →</Button>{/* G436 */}
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* ── Add External Agent Framework ─────────────────────────
        * V226 / CONN-SCOPE Phase G — converted from inline Modal to V2
        * Slideout per §5.5/§3.13 (configurator pattern: 12+ fields,
        * conditional auth config, embedded TenantScopePicker for cross-
        * tenant share). The sticky header + footer pattern keeps the
        * primary CTA reachable as the form scrolls. */}
      <Slideout
        isOpen={showCreate}
        onClose={creating ? null : () => setShowCreate(false)}
        dismissOnBackdrop={!creating}
        width={720}
        ariaLabel="Add External Agent Framework"
        header={
          <div className="px-4 py-3 border-b border-v2-border-subtle">
            <div className="flex items-center gap-2 mb-1">
              <Plus size={14} className="text-v2-action" aria-hidden="true" />
              <span className="text-[13px] font-semibold text-v2-ink">Add External Agent Framework</span>
            </div>
            <p className="text-[11px] text-v2-ink-muted leading-snug m-0">
              Credentials + endpoint binding for an external agent platform.
              After creation, register your external agents against this framework.
            </p>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2 px-4 py-3 border-t border-v2-border-subtle bg-v2-page">
            <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
            <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
              onClick={handleCreate} loading={creating} disabled={creating}>
              {creating ? 'Creating…' : 'Create Connector'}
            </Button>
          </div>
        }
      >
        {showCreate && (
        <div className="px-4 py-4">
          <form onSubmit={handleCreate} className="flex flex-col gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Connector Name *</label>
              <input type="text" value={form.name} autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                placeholder="e.g., Production Anthropic" />
            </div>
            <div>
                <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Framework *</label>
                <div className="mt-1">
                  <EntityPicker
                    ariaLabel="Framework"
                    items={FRAMEWORK_CHOICES}
                    value={form.frameworkChoice}
                    groups={FRAMEWORK_TABS}
                    getGroupId={(x) => x.group}
                    getId={(x) => x.id}
                    getLabel={(x) => x.label}
                    getSublabel={(x) => x.desc}
                    getSortKey={() => ''}
                    searchPlaceholder="Search frameworks & agents"
                    onChange={(id) => {
                      const c = FRAMEWORK_CHOICES.find((x) => x.id === id);
                      if (!c) return;
                      setForm((f) => ({
                        ...f,
                        frameworkChoice: id,
                        platformType: c.platformType,
                        // Seed the auth-config template with the chosen mode/vendor so
                        // the operator doesn't hand-type it; picking a framework starts
                        // its config fresh.
                        authConfig: c.preset ? JSON.stringify(c.preset, null, 2) : '',
                      }));
                    }}
                  />
                </div>
            </div>

            {/* Coded-SDK frameworks have no inbound endpoint and no connector-held secret —
              * show an explainer and drop the proxied fields (endpoint / auth / routing).
              * Everything else (owner, scope, governance defaults) is shared. */}
            {isCodedFramework(form.platformType) ? (
              <div className="rounded-v2-sm border border-v2-border-subtle bg-v2-surface-subtle px-3 py-2">
                <p className="text-[11.5px] text-v2-ink-muted leading-snug">
                  <strong className="text-v2-ink">Coded-SDK framework.</strong> This agent runs in your process and is
                  protected in-process by the Agentlets SDK (the CODED path) — it calls the gateway’s pre-check / post-check.
                  There is <strong>no inbound endpoint and no connector-held secret</strong>: the agent’s scoped key
                  (<code className="font-mono-v2">agt_sk_</code>) is minted when you register the ExteLet against this framework.
                </p>
              </div>
            ) : (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                  {form.platformType === 'CUSTOM_MCP' ? 'MCP Server URL' : 'Base URL *'}
                </label>
                <input type="text" value={form.baseUrl}
                  onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
                  className="text-[12.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  placeholder={form.platformType === 'OPENAI' ? 'https://api.openai.com/v1'
                            : form.platformType === 'ANTHROPIC' ? 'https://api.anthropic.com'
                            : form.platformType === 'CUSTOM_MCP' ? 'https://mcp.example.com'
                            : 'https://api.example.com/v1'} />
              </div>
            )}

            {isCodedFramework(form.platformType) ? (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                  Default Model <span className="text-v2-ink-muted font-normal normal-case tracking-normal">(optional protection hint)</span>
                </label>
                <input type="text" value={form.defaultModel}
                  onChange={(e) => setForm({ ...form, defaultModel: e.target.value })}
                  className="text-[12.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  placeholder="e.g., claude-sonnet-4-6 (optional)" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Auth Method</label>
                  <select value={form.authMethod}
                    onChange={(e) => setForm({ ...form, authMethod: e.target.value })}
                    className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1 cursor-pointer">
                    {AUTH_OPTIONS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                    {form.platformType === 'CUSTOM_MCP' ? 'Tool Namespace / Model' : 'Default Model'}
                  </label>
                  <input type="text" value={form.defaultModel}
                    onChange={(e) => setForm({ ...form, defaultModel: e.target.value })}
                    className="text-[12.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                    placeholder={form.platformType === 'OPENAI' ? 'gpt-4-turbo'
                              : form.platformType === 'ANTHROPIC' ? 'claude-sonnet-4-6'
                              : 'model-id'} />
                </div>
              </div>
            )}

            {!isCodedFramework(form.platformType) && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Auth Config (JSON)</label>
              <textarea value={form.authConfig}
                onChange={(e) => setForm({ ...form, authConfig: e.target.value })}
                rows={3}
                className="text-[11.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                placeholder='{"apiKey": "sk-..."}  or  {"apiKey": "vault://gcp/projects/foo/secrets/openai-key"}' />
              <p className="text-[10.5px] text-v2-ink-muted mt-1">
                Sensitive fields (apiKey · bearerToken · accessToken · clientSecret · privateKey · password · serviceAccountJson) are
                <strong> automatically moved to the configured secret vault</strong> on save and replaced with a <code className="font-mono-v2">vault://&lt;backend&gt;/&lt;path&gt;</code>
                reference. The platform never persists plaintext credentials. Paste an existing vault reference to reuse one.
              </p>
            </div>
            )}

            {/* Governance defaults. Coded frameworks keep Max Tokens + Timeout (SDK hints);
              * Rate/Concurrency are proxied-routing limits and only apply to dispatched agents. */}
            <div className={isCodedFramework(form.platformType)
              ? 'grid grid-cols-2 gap-3'
              : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'}>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Max Tokens</label>
                <input type="number" value={form.defaultMaxTokens}
                  onChange={(e) => setForm({ ...form, defaultMaxTokens: e.target.value })}
                  className="text-[12px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Timeout (s)</label>
                <input type="number" value={form.defaultTimeoutSeconds}
                  onChange={(e) => setForm({ ...form, defaultTimeoutSeconds: e.target.value })}
                  className="text-[12px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1" />
              </div>
              {!isCodedFramework(form.platformType) && (<>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Rate (RPM)</label>
                <input type="number" value={form.rateLimitRpm}
                  onChange={(e) => setForm({ ...form, rateLimitRpm: e.target.value })}
                  className="text-[12px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Concurrency</label>
                <input type="number" value={form.maxConcurrency}
                  onChange={(e) => setForm({ ...form, maxConcurrency: e.target.value })}
                  className="text-[12px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1" />
              </div>
              </>)}
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Description</label>
              <textarea value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="text-[11.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                placeholder="Notes / scope / owner…" />
            </div>

            {/* CONN-OWNER — Owner Org + Tenant picker. Always rendered;
              * for Tenant Admin it auto-fills + collapses to a read-only
              * summary. Required for submit. */}
            <div className="mt-2 pt-3 border-t border-v2-border-subtle">
              <ConnectorOwnerPicker
                value={{ ownerOrgId: form.ownerOrgId, ownerTenantId: form.ownerTenantId }}
                onChange={({ ownerOrgId: nextOrg, ownerTenantId: nextTenant }) => setForm({
                  ...form,
                  ownerOrgId: nextOrg || '',
                  ownerTenantId: nextTenant || '',
                  /* When owner-org changes, drop any cross-tenant scope
                   * grants — they were scoped to the prior owner-org's
                   * sibling tenants and would be invalid against the new
                   * org's tenant set. Forces operator to re-pick. */
                  scopeGrants: nextOrg !== form.ownerOrgId ? [] : form.scopeGrants,
                })}
                sessionRole={session?.role}
                sessionOrgId={orgId}
                sessionTenantId={tenantId}
                readOnly={creating}
              />
            </div>

            {/* V226 / CONN-SCOPE — tenant scope picker. Only Org/Super/
              * Platform Admin sees this; Tenant Admin's create stays
              * single-tenant. The BE accepts scopeTenantIds in the
              * payload only from grant-capable roles (returns 403
              * otherwise — the FE hides the picker to avoid surfacing a
              * field that would fail on submit). Now scoped to the
              * OWNER org from the ConnectorOwnerPicker — not session. */}
            {canGrantScope && form.ownerOrgId && (
              <div className="mt-2 pt-3 border-t border-v2-border-subtle">
                <TenantScopePicker
                  value={form.scopeGrants}
                  onChange={(grants) => setForm({ ...form, scopeGrants: grants })}
                  ownerTenantId={form.ownerTenantId}
                  orgId={form.ownerOrgId}
                  ariaLabel="Cross-tenant share scope for new connector"
                />
              </div>
            )}
          </form>
        </div>
        )}
      </Slideout>

      {/* G205 — Configure (edit) connector modal */}
      {showEdit && editForm && (
        <Modal isOpen onClose={() => !editSaving && setShowEdit(false)} title="Configure External Agent Framework" width={600}>
          <p className="text-[12.5px] text-v2-ink-secondary mb-3">
            Update routing, defaults, rate limits, and credentials. Framework is fixed at creation. Sensitive credential
            fields are re-encrypted to the secret vault on save and replaced with a <code className="font-mono-v2">vault://</code> reference.
          </p>

          <form onSubmit={submitEdit} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Connector Name *</label>
                <input type="text" value={editForm.name} autoFocus
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  disabled={editSaving} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Framework (fixed)</label>
                <input type="text" value={editForm.platformType}
                  className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1 bg-v2-muted text-v2-ink-muted"
                  disabled readOnly />
              </div>
            </div>

            {isCodedFramework(editForm.platformType) ? (
              <div className="rounded-v2-sm border border-v2-border-subtle bg-v2-surface-subtle px-3 py-2">
                <p className="text-[11.5px] text-v2-ink-muted leading-snug">
                  <strong className="text-v2-ink">Coded-SDK framework.</strong> Protected in-process via the SDK — no inbound
                  endpoint and no connector-held secret. The ExteLet’s <code className="font-mono-v2">agt_sk_</code> key carries its credentials.
                </p>
              </div>
            ) : (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Base URL *</label>
                <input type="text" value={editForm.baseUrl}
                  onChange={(e) => setEditForm({ ...editForm, baseUrl: e.target.value })}
                  className="text-[12.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  disabled={editSaving} />
              </div>
            )}

            {isCodedFramework(editForm.platformType) ? (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                  Default Model <span className="text-v2-ink-muted font-normal normal-case tracking-normal">(optional protection hint)</span>
                </label>
                <input type="text" value={editForm.defaultModel}
                  onChange={(e) => setEditForm({ ...editForm, defaultModel: e.target.value })}
                  className="text-[12.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  disabled={editSaving} />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Auth Method</label>
                  <select value={editForm.authMethod}
                    onChange={(e) => setEditForm({ ...editForm, authMethod: e.target.value })}
                    className="text-[12.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1 cursor-pointer"
                    disabled={editSaving}>
                    {AUTH_OPTIONS.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Default Model</label>
                  <input type="text" value={editForm.defaultModel}
                    onChange={(e) => setEditForm({ ...editForm, defaultModel: e.target.value })}
                    className="text-[12.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                    disabled={editSaving} />
                </div>
              </div>
            )}

            {!isCodedFramework(editForm.platformType) && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Auth Config (JSON)</label>
              <textarea value={editForm.authConfig}
                onChange={(e) => setEditForm({ ...editForm, authConfig: e.target.value })}
                rows={3}
                className="text-[11.5px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                placeholder='{"apiKey": "vault://gcp/projects/foo/secrets/openai-key"}'
                disabled={editSaving} />
              <p className="text-[10.5px] text-v2-ink-muted mt-1">
                Existing credentials are stored as <code className="font-mono-v2">vault://</code> references and are not displayed here.
                Paste a new value to rotate, or leave blank to keep existing credentials.
              </p>
            </div>
            )}

            <div className={isCodedFramework(editForm.platformType)
              ? 'grid grid-cols-2 gap-3'
              : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'}>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Max Tokens</label>
                <input type="number" value={editForm.defaultMaxTokens}
                  onChange={(e) => setEditForm({ ...editForm, defaultMaxTokens: e.target.value })}
                  className="text-[12px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  disabled={editSaving} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Timeout (s)</label>
                <input type="number" value={editForm.defaultTimeoutSeconds}
                  onChange={(e) => setEditForm({ ...editForm, defaultTimeoutSeconds: e.target.value })}
                  className="text-[12px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  disabled={editSaving} />
              </div>
              {!isCodedFramework(editForm.platformType) && (<>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Rate (RPM)</label>
                <input type="number" value={editForm.rateLimitRpm}
                  onChange={(e) => setEditForm({ ...editForm, rateLimitRpm: e.target.value })}
                  className="text-[12px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  disabled={editSaving} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-v2-ink-muted font-semibold">Concurrency</label>
                <input type="number" value={editForm.maxConcurrency}
                  onChange={(e) => setEditForm({ ...editForm, maxConcurrency: e.target.value })}
                  className="text-[12px] font-mono-v2 px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                  disabled={editSaving} />
              </div>
              </>)}
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Description</label>
              <textarea value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={2}
                className="text-[11.5px] px-2 py-1.5 border border-v2-border rounded-v2-sm w-full mt-1"
                disabled={editSaving} />
            </div>

            {editError && (
              <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft text-v2-danger-strong p-3 text-[12.5px]">
                {editError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-v2-border-subtle">
              <Button variant="ghost" size="sm" onClick={() => setShowEdit(false)} disabled={editSaving}>Cancel</Button>
              <Button variant="primary" size="sm" iconLeft={<Settings size={13} />}
                onClick={submitEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* V226 / CONN-SCOPE — row-level Assign-to-tenants slideout.
        * Opens from the Users icon in the row actions. The BE assign
        * + revoke calls hit /api/v1/gel/connectors/{id}/scope-tenants. */}
      <ConnectorAssignTenantsSlideout
        isOpen={!!assignTenantsRow}
        onClose={() => setAssignTenantsRow(null)}
        onSaved={(newGrants) => {
          // Optimistic local update so the row pill re-counts before the
          // next list reload. Full reload also fires for safety.
          if (assignTenantsRow) {
            const newTenantIds = (newGrants || []).map((g) => g.tenantId);
            setConnectors((prev) => prev.map((c) =>
              c.id === assignTenantsRow.id
                ? {
                    ...c,
                    __sharedWithTenantIds: newTenantIds,
                    __sharedWithGrants: newGrants || [],
                  }
                : c
            ));
          }
          reload();
        }}
        connector={assignTenantsRow}
        /* V227 — pass the rich grant shape so per-instance restrictions
         * round-trip correctly through the picker. */
        currentScope={assignTenantsRow?.__sharedWithGrants || []}
        orgId={orgId}
        apiAssignGrants={api.gelAssignConnectorTenantGrants}
        apiRevokeTenant={api.gelRevokeConnectorTenantScope}
        resourceLabel="External Agent Framework"
      />
    </div>
  );
}

export default function ResolvedGelPlatformConnectorsPage(props) {
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy) return <GelPlatformConnectorsPage {...props} />;
  return <GelPlatformConnectorsPageV2Inner {...props} />;
}

export { GelPlatformConnectorsPageV2Inner };
